import { BannerType, Languages } from '@/constants'
import { useLocalStorage } from '@/hooks'
import { useCookie } from '@/hooks/useCookie'
import { useCookie1Y } from '@/hooks/useCookie1Y'
import type {
  BannerList,
  Keywords,
  Menu,
  ProductCompare,
  ProductViewed,
  Province,
  Region,
  SystemConfig,
} from '@/types'
import { api } from '@/utils'
import { getSaleRegion } from '@/utils/endpoint'
import constate from 'constate'
import { getCookie, setCookie } from 'cookies-next'
import { useEffect, useMemo, useState } from 'react'
import useLocalStorageState from 'use-local-storage-state'

type Props = {
  stores: Province[] | null
  regions: Region[]
  area: string | null
  language: Languages
  systemConfig: SystemConfig
  menus: Menu[] | null
  quickMenu: Menu[] | null
  bottomMenu: Menu[] | null
  banner: BannerList[] | null
  keywords: Keywords[] | null
}

const StoreContainer = (props: Props) => {
  const [isOpenPopupLocation, setIsOpenPopupLocation] = useState(false)
  const [isDisplayAreaPopup, setIsDisplayAreaPopup] = useLocalStorage<boolean>(
    'areaPopup',
    true
  )
  const currentRegion = getCookie('Area-Code')
  const defaultSaleRegion = process.env.NEXT_PUBLIC_SALE_REGION_DEFAULT
  const [productCompares, setProductCompares] = useLocalStorageState<
    ProductCompare[]
  >('productCompare', { defaultValue: [{}, {}, {}] })

  const [productViewed, setProductViewed] = useLocalStorage<ProductViewed[]>(
    'productViewed',
    []
  )
  const [isOpenComparePopup, setIsOpenComparePopup] = useState(false)
  const [isOpenAddComparePopup, setIsOpenAddComparePopup] = useState(false)
  const [isOpenComingSoon, setIsOpenComingSoon] = useState(false)
  const [catViewId, setCatViewId] = useState<string | null>(null)
  const [stores, setStores] = useState<Province[]>([])
  const [regions, setRegions] = useState<Region[]>([])
  const [megaMenu, setMegaMenu] = useState<Menu[]>(props.menus || [])
  const [quickMenu, setQuickMenu] = useState<Menu[]>(props.quickMenu || [])
  const [bottomMenu] = useState<Menu[]>(props.bottomMenu || [])
  const [banner, setBanner] = useState<BannerList[]>(props.banner || [])
  const [province, setProvince] = useCookie<string | null>('province', null)
  const [destinationCode, setDestinationCode] = useCookie<string | null>(
    'destinationCode',
    null
  )
  const [isPreOrder, setIsPreOrder] = useCookie<string | null>(
    'isPreOrder',
    null
  )
  const [keywords, setKeywords] = useState<Keywords[]>(props.keywords || [])
  const [provinceOfRegion, setProvinceOfRegion] = useState<Region[] | null>(
    null
  )
  const [regionChange, setRegionChange] = useState<string>('')
  const [areaType, setAreaType] = useState<'old' | 'new'>('old')
  const [selectedLang, setSelectedLang] = useCookie<
    Languages | undefined | null
  >('language', props.language || Languages.VN)
  const [area, setArea] = useCookie1Y<string | null>('area', null)
  const [areaId, setAreaId] = useCookie1Y<string | null>('area-id', null)

  const [saleRegion, setSaleRegion] = useCookie1Y<string | null>(
    'Area-Code',
    null
  )

  const [isChangeRegionModal, setIsChangeRegionModal] = useState(false)

  // Khởi tạo giá trị mặc định khi truy cập trang lần đầu
  useEffect(() => {
    const DEFAULT_PROVINCE = process.env.NEXT_PUBLIC_PROVINCE_DEFAULT || '01'

    // area-type: mặc định 'old'
    if (!localStorage.getItem('area-type')) {
      localStorage.setItem('area-type', 'old')
    }
    if (!getCookie('area-type')) {
      setCookie('area-type', 'old', { maxAge: 2147483647 })
    }

    // province: mặc định '01'
    if (!localStorage.getItem('province')) {
      localStorage.setItem('province', DEFAULT_PROVINCE)
    }
    if (!getCookie('province')) {
      setCookie('province', DEFAULT_PROVINCE, { maxAge: 2147483647 })
    }
  }, [])

  useEffect(() => {
    const checkShowArea = localStorage.getItem('area-popup-seen')
    const checkHeaderRegion = getCookie('Area-Code')

    if (checkShowArea !== 'true' && !checkHeaderRegion) {
      setIsOpenPopupLocation(true)
    }
  }, [])
  useEffect(() => {
    if (regions && regions.length > 0) {
      // Chỉ khởi tạo nếu chưa có Area-Code trong cookie
      // Không override khi user đã chọn địa chỉ cụ thể
      const existingAreaCode = getCookie('Area-Code')
      if (!existingAreaCode) {
        // Tìm region khớp với currentRegion (Area-Code hiện tại)
        const matchedRegion = regions.find(e => e.code === currentRegion)
        // Fallback: dùng region đầu tiên từ API (có định dạng MB1/MT1/MN1)
        // hoặc defaultSaleRegion từ env nếu có
        const fallbackCode =
          defaultSaleRegion && regions.find(e => e.code === defaultSaleRegion)
            ? defaultSaleRegion
            : regions[0]?.code
        setSaleRegion(matchedRegion?.code || fallbackCode || 'MB1')
      }
    }
  }, [regions])

  useEffect(() => {
    if (props.menus) {
      setMegaMenu(props.menus)
    }
  }, [props.menus])

  useEffect(() => {
    if (props.quickMenu && !quickMenu) {
      setQuickMenu(props.quickMenu)
    }
  }, [props.quickMenu])
  useEffect(() => {
    if (props.keywords && keywords.length == 0) {
      setKeywords(props.keywords)
    }
  }, [props.keywords])

  useEffect(() => {
    if (props.banner) {
      setBanner(props.banner)
    }
  }, [props.banner])

  useEffect(() => {
    const fetchRegions = async () => {
      const res = await getSaleRegion()
      if (res) {
        setRegions(res || [])
      }
    }

    fetchRegions()
  }, [])

  useEffect(() => {
    if (props.language && !selectedLang) {
      setSelectedLang(props.language)
    }
  }, [props.language])

  const topBanner = useMemo(() => {
    const topBanner = banner.find(e => e.type === BannerType.TOP_BAR)
    return topBanner
  }, [banner])

  const sideBarDataBanner = useMemo(() => {
    const sideBarDataBanner = banner.find(e => e.type === BannerType.SIDEBAR)
    return sideBarDataBanner ? sideBarDataBanner.items : []
  }, [banner])

  useEffect(() => {
    const currentAreaType = getCookie('area-type') || 'old'
    api(`api/sale-region/province?areaType=${currentAreaType}`, {
      baseURL: process.env.NEXT_PUBLIC_ECOMMERCE_API_URL,
      method: 'get',
    }).then(async result => {
      if (result instanceof Error) {
        return
      }
      setProvinceOfRegion(result.data)
    })
  }, [])
  return {
    province,
    setProvince,
    area,
    setArea,
    areaId,
    setAreaId,
    setSelectedLang,
    setDestinationCode,
    destinationCode,
    selectedLang,
    stores,
    banner,
    megaMenu,
    quickMenu,
    topBanner,
    sideBarDataBanner,
    keywords,
    setStores,
    isOpenPopupLocation,
    setIsOpenPopupLocation,
    isDisplayAreaPopup,
    bottomMenu,
    setIsDisplayAreaPopup,
    isOpenComingSoon,
    setIsOpenComingSoon,
    productCompares,
    setProductCompares,
    isOpenComparePopup,
    setIsOpenComparePopup,
    isOpenAddComparePopup,
    setIsOpenAddComparePopup,
    productViewed,
    setProductViewed,
    catViewId,
    setCatViewId,
    isPreOrder,
    setIsPreOrder,
    areaType,
    setAreaType,
    saleRegion,
    setSaleRegion,
    regions,
    setRegions,
    currentRegion,
    provinceOfRegion,
    setProvinceOfRegion,
    isChangeRegionModal,
    setIsChangeRegionModal,
    regionChange,
    setRegionChange,
  }
}

export const [StoreProvider, useStore] = constate(StoreContainer)
